import { useState, useRef, useEffect } from "react";

// ─── FONTS ────────────────────────────────────────────────────────────────
const _fl = document.createElement("link");
_fl.rel = "stylesheet";
_fl.href = "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Oswald:wght@500;600;700&display=swap";
document.head.appendChild(_fl);

// ─── PORTLAND DESIGN CONDITIONS ───────────────────────────────────────────
const PORTLAND = {
  climate_zone: "4C Marine",
  design_summer_temp: 88,
  design_winter_temp: 22,
  heating_degree_days: 4400,
  cooling_degree_days: 300,
};

// ─── MANUAL J CALCULATION ────────────────────────────────────────────────
// Portland climate 4C Marine: ~12 BTU/sqft cooling, ~24 BTU/sqft heating baseline
// Multipliers are intentionally conservative — Portland's mild climate doesn't need aggressive factors
function calcLoads({ sqft, stories, insulation, windows }) {
  const sf = parseFloat(sqft) || 0;
  const st = parseFloat(stories) || 1;
  const iM = { "Excellent / High-Performance": 0.80, "Good": 0.90, "Average": 1.00, "Below Average": 1.10, "Poor": 1.20 }[insulation] || 1.00;
  const wM = { "Good / Double-pane Low-E": 0.95, "Average": 1.00, "Single-pane / Minimal": 1.10 }[windows] || 1.00;
  const sM = st <= 1 ? 1.00 : st <= 1.5 ? 1.02 : st <= 2 ? 1.04 : 1.06;
  const coolBtu = Math.round(sf * 12 * iM * wM * sM / 100) * 100;
  const heatBtu = Math.round(sf * 24 * iM * wM * sM / 100) * 100;
  return { coolBtu, heatBtu, coolTons: roundTons(coolBtu / 12000), heatTons: roundTons(heatBtu / 12000) };
}

function roundTons(raw) {
  const w = Math.floor(raw), d = raw - w;
  const s = d < 0.1 ? w : d <= 0.5 ? w + 0.5 : w + 1;
  return Math.max(2, Math.min(5, s));
}
function roundTonsFull(t) {
  // RD18 only in full tons: 2, 3, 4, 5
  return Math.max(2, Math.min(5, Math.ceil(t)));
}
function sfx(t) {
  // Rheem MBH suffix: 2T->24, 2.5T->30, 3T->36 …
  return String(Math.round(t * 12)).padStart(2, "0");
}

// ─── BUILD-YEAR → INSULATION / WINDOWS ────────────────────────────────────
function deriveFromYear(y) {
  y = parseInt(y);
  if (isNaN(y)) return null;
  if (y < 1960) return { ins: "Poor",              win: "Single-pane / Minimal",   insNote: "Pre-1960 — minimal or no insulation",       winNote: "Single-pane wood or aluminum frames" };
  if (y < 1975) return { ins: "Below Average",      win: "Single-pane / Minimal",   insNote: "Pre-energy-crisis — R-11 walls, R-19 attic", winNote: "Single-pane aluminum, very leaky" };
  if (y < 1985) return { ins: "Below Average",      win: "Average",                 insNote: "Post-oil-crisis — R-13 walls, R-30 attic",   winNote: "Early double-pane appearing" };
  if (y < 1993) return { ins: "Average",            win: "Average",                 insNote: "Model Energy Code — R-13 walls, R-30 attic", winNote: "Double-pane standard" };
  if (y < 2004) return { ins: "Average",            win: "Average",                 insNote: "IECC 1998 — R-13 walls, R-38 attic",         winNote: "Low-e double-pane emerging" };
  if (y < 2012) return { ins: "Good",               win: "Average",                 insNote: "IECC 2003-09 — R-21 walls, R-49 attic",      winNote: "Low-e double-pane standard" };
  if (y < 2020) return { ins: "Good",               win: "Good / Double-pane Low-E",insNote: "IECC 2012-18 — R-49+ attic, cont. insulation",winNote: "High-perf low-e, thermally broken frames" };
  return          { ins: "Excellent / High-Performance", win: "Good / Double-pane Low-E", insNote: "IECC 2021 — R-60 attic, near zero-energy", winNote: "Triple or super-insulated double-pane" };
}

// ─── SYSTEM DETECTION ─────────────────────────────────────────────────────
function detectSystem(venting, outdoor) {
  if (outdoor === "none")   return { type:"furnace-only", label:"Gas Furnace (No Outdoor Unit)", icon:"🏠", desc:"No outdoor unit right now — we'll add a matched one at install.", color:"#166534", bg:"#F0FDF4", border:"#86EFAC" };
  if (venting === "none")   return { type:"heatpump",     label:"All-Electric Heat Pump",       icon:"⚡", desc:"Fully electric — heats and cools with one outdoor unit. No gas at all.", color:"#1D4ED8", bg:"#EFF6FF", border:"#93C5FD" };
  if (venting === "metal")  return { type:"furnace-80",   label:"80% Gas Furnace + " + (outdoor==="yes"?"Heat Pump":"Central AC"), icon:"🔥", desc:outdoor==="yes"?"80% furnace with a heat pump outdoor unit — classic dual-fuel setup.":"Standard 80% gas furnace with central AC. Metal flue is the giveaway.", color:"#C2410C", bg:"#FFF7ED", border:"#FED7AA" };
  if (venting === "pvc")    return { type:"furnace-90",   label:"90%+ Gas Furnace + " + (outdoor==="yes"?"Heat Pump":"Central AC"), icon:"💎", desc:outdoor==="yes"?"High-efficiency condensing furnace with heat pump — the dual-fuel dream.":"High-efficiency 90%+ furnace with central AC. White PVC pipes are the tell.", color:"#5B21B6", bg:"#F5F3FF", border:"#C4B5FD" };
  return null;
}

// ─── PRICING ──────────────────────────────────────────────────────────────
const PRICE = {
  "R801V+RA15": { b:[8800,11200],  a:[600,700]  },
  "R801V+RP15": { b:[9400,12000],  a:[650,750]  },
  "R802V+RA15": { b:[10200,12800], a:[650,750]  },
  "R802V+RP15": { b:[10800,13600], a:[700,800]  },
  "R951V+RA15": { b:[11800,14800], a:[700,800]  },
  "R951V+RP15": { b:[12800,15800], a:[750,850]  },
  "R98MV+RD18": { b:[18800,22000], a:[900,1100] },
  "RH2V+RP15":  { b:[10800,13600], a:[700,800]  },
  "RH2V+RD18":  { b:[19000,22000], a:[900,1000] },
  "R801V":       { b:[3500,4500],   a:[0,0]      },
  "R802V":       { b:[4500,5800],   a:[0,0]      },
  "R951V":       { b:[5200,6800],   a:[0,0]      },
  "R98MV":       { b:[7500,9500],   a:[0,0]      },
  "RH2V":        { b:[2800,3800],   a:[0,0]      },
};
function price(key, t) {
  const p = PRICE[key]; if (!p) return "Call for pricing";
  const steps = Math.max(0, (t - 2) / 0.5);
  const lo = Math.round((p.b[0] + steps * p.a[0]) / 100) * 100;
  const hi = Math.round((p.b[1] + steps * p.a[1]) / 100) * 100;
  return "$" + lo.toLocaleString() + " – $" + hi.toLocaleString() + " installed";
}

// ─── RHEEM RECOMMENDATIONS ────────────────────────────────────────────────
// Rules:
//   PVC venting → only condensing furnaces (R951V, R98MV) — no R801V/R802V
//   Metal venting → 80% furnaces (R801V, R802V), Best tier adds R98MV w/ PVC note
//   No venting → all-electric (RH2V + RP15/RD18)
//   No outdoor unit → indoor only (furnace or air handler)
//   RD18 → full tons only (roundTonsFull)
function getRheem({ coolTons, heatBtu, systemType, venting, outdoor }) {
  const t   = coolTons;
  const sx  = sfx(t);
  const rdT = roundTonsFull(t);
  const rdX = sfx(rdT);
  const hp  = outdoor === "yes" && (venting === "metal" || venting === "pvc");
  const ouM = hp ? "RP15" : "RA15";
  const ouL = hp ? "Classic Plus Heat Pump" : "Classic Plus AC";

  // ── Indoor only (no outdoor unit) ──────────────────────────────────────
  if (systemType === "furnace-only") {
    return [
      { tier:"Good",   label:"Budget-Friendly", color:"#1E4D30", indoor:"R801V"+sx, indoorSeries:"Classic Series",   outdoor:null, afue:80,  seer2:null, hspf2:null,
        bullets:["80% AFUE reliable heat","Variable-speed ECM blower","Outdoor unit matched at install"],
        why:"Gets your furnace replaced now. We hand-pick the perfect matched outdoor unit when we install.",
        priceStr: price("R801V", t) },
      { tier:"Better", label:"Most Popular",     color:"#2A5C3F", indoor:"R951V"+sx, indoorSeries:"Classic Series",   outdoor:null, afue:96,  seer2:null, hspf2:null,
        bullets:["96% AFUE — noticeably lower gas bills","Single-stage quiet operation","Pays back fast in Portland winters"],
        why:"Our most popular choice. Portland winters make that 96% AFUE pay for itself quicker than you'd think.",
        priceStr: price("R951V", t) },
      { tier:"Best",   label:"Top Efficiency",   color:"#D95C0A", indoor:"R98MV"+sx, indoorSeries:"Prestige Series",  outdoor:null, afue:98,  seer2:null, hspf2:null,
        bullets:["98% AFUE — top of the line","Modulating 40–100% output","Rock-steady temps, whisper quiet"],
        why:"Maximum comfort, minimum gas bill. Modulating heat means no blasts of hot air — just perfect warmth.",
        priceStr: price("R98MV", t) },
    ];
  }

  // ── All-electric (no gas venting) ──────────────────────────────────────
  if (systemType === "heatpump") {
    return [
      { tier:"Good",   label:"Great Efficiency", color:"#1E4D30", indoor:"RH2V"+sx, indoorSeries:"Endeavor Series",  outdoor:"RP15"+sx, outdoorSeries:"Classic Plus Heat Pump", afue:null, seer2:15.2, hspf2:7.8,
        bullets:["All-electric heat pump","15.2 SEER2 cooling efficiency","7.8 HSPF2 heating — great for Portland"],
        why:"Solid all-electric performance. Portland's mild winters are exactly what heat pumps love.",
        priceStr: price("RH2V+RP15", t) },
      { tier:"Best",   label:"Premium Electric",  color:"#D95C0A", indoor:"RH2V"+sx, indoorSeries:"Endeavor Series",  outdoor:"RD18"+rdX, outdoorSeries:"Prestige Heat Pump",    afue:null, seer2:19,   hspf2:10.5,
        bullets:["Up to 19 SEER2 — top efficiency","Fully variable compressor","Lowest electric bill of any system"],
        why:"The ultimate all-electric system. Fully variable speed means it whispers along at exactly the right output all day.",
        priceStr: price("RH2V+RD18", t) },
    ];
  }

  // ── PVC venting → condensing furnaces only ──────────────────────────────
  if (systemType === "furnace-90") {
    return [
      { tier:"Good",   label:"High Efficiency",  color:"#1E4D30", indoor:"R951V"+sx, indoorSeries:"Classic Series",  outdoor:ouM+sx, outdoorSeries:ouL, afue:96, seer2:15.2, hspf2:hp?7.8:null,
        bullets:["96% AFUE condensing furnace","15.2 SEER2 cooling","Like-for-like PVC vent replacement"],
        why:"Perfect like-for-like upgrade — same PVC venting, big jump in efficiency.",
        priceStr: price("R951V+"+ouM, t) },
      { tier:"Best",   label:"Ultimate Comfort",  color:"#D95C0A", indoor:"R98MV"+sx, indoorSeries:"Prestige Series", outdoor:"RD18"+rdX, outdoorSeries:"Prestige Heat Pump", afue:98, seer2:19, hspf2:10.5,
        bullets:["98% AFUE modulating furnace","Up to 19 SEER2 variable heat pump","Whisper-quiet year-round comfort"],
        why:"The best system we offer. Modulating furnace + variable heat pump = rock-steady temps and the lowest utility bills possible.",
        priceStr: price("R98MV+RD18", t) },
    ];
  }

  // ── Metal venting → 80% furnaces + Best w/ PVC note ───────────────────
  // systemType === "furnace-80" or fallback
  return [
    { tier:"Good",   label:"Dependable Value",  color:"#1E4D30", indoor:"R801V"+sx, indoorSeries:"Classic Series",    outdoor:ouM+sx, outdoorSeries:ouL, afue:80, seer2:15.2, hspf2:hp?7.8:null,
      bullets:["80% AFUE proven reliability","Variable-speed ECM blower","Like-for-like metal flue replacement"],
      why:"Solid workhorse system. Great if you want the most straightforward replacement at the best price.",
      priceStr: price("R801V+"+ouM, t) },
    { tier:"Better", label:"Most Popular",       color:"#2A5C3F", indoor:"R802V"+sx, indoorSeries:"Classic Plus Series",outdoor:ouM+sx, outdoorSeries:ouL, afue:80, seer2:15.2, hspf2:hp?7.8:null,
      bullets:["80% AFUE two-stage operation","Better comfort & humidity control than single-stage","Quieter than single-stage on mild days"],
      why:"Two-stage heat means better comfort on those typical Portland cool-but-not-cold days.",
      priceStr: price("R802V+"+ouM, t) },
    { tier:"Best",   label:"Ultimate Upgrade",   color:"#D95C0A", indoor:"R98MV"+sx, indoorSeries:"Prestige Series",   outdoor:"RD18"+rdX, outdoorSeries:"Prestige Heat Pump", afue:98, seer2:19, hspf2:10.5,
      bullets:["98% AFUE — new PVC venting included","Up to 19 SEER2 variable heat pump","Biggest efficiency leap available"],
      why:"A full step-up: we replace your flue with new PVC and install the best system Rheem makes. The utility savings are real.",
      priceStr: price("R98MV+RD18", t) },
  ];
}

// ─── EMAIL BUILDER ────────────────────────────────────────────────────────
function buildEmail({ contactName, email, phone, note, calc, system, options }) {
  const t = calc.coolTons;
  const lines = [
    "=== BULL MOUNTAIN HVAC ESTIMATE INQUIRY ===",
    "",
    "CONTACT INFO:",
    "  Name:  " + (contactName || "Not provided"),
    "  Email: " + (email || "Not provided"),
    "  Phone: " + (phone || "Not provided"),
    "",
    "HOME DETAILS:",
    "  Square Footage: " + calc.sqft + " sqft",
    "  Year Built:     " + calc.year,
    "  Stories:        " + calc.stories,
    "  Insulation:     " + calc.ins,
    "  Windows:        " + calc.win,
    "",
    "CURRENT SYSTEM:",
    "  " + (system?.label || "Unknown"),
    "",
    "LOAD ESTIMATE (Portland climate zone 4C, 88F/22F design):",
    "  Cooling: " + t + " tons  (" + calc.coolBtu.toLocaleString() + " BTU/hr)",
    "  Heating: " + calc.heatBtu.toLocaleString() + " BTU/hr",
    "",
    "RHEEM OPTIONS SHOWN:",
  ];
  options.forEach(o => {
    lines.push("  [" + o.tier + "] " + o.indoor + (o.outdoor ? " + " + o.outdoor : " (indoor only)") + " — " + o.priceStr);
  });
  if (note) { lines.push("", "CLIENT NOTE:", "  " + note); }
  lines.push("", "===========================================");
  return lines.join("\n");
}

// ─── STYLES ───────────────────────────────────────────────────────────────
const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --gd: #163422; --gb: #1E4D30; --gc: #2A6642;
    --or: #D95C0A; --ol: #F07A2A;
    --tx: #1A2E1E; --ts: #4A6355;
    --br: #D5E8DC; --bg: #F5EFE6; --bg2: #EDE5D8;
  }
  body { background: var(--bg); font-family: 'Nunito', sans-serif; }

  /* HERO */
  .hero { background: linear-gradient(150deg, var(--gd), #0F2318); padding: 44px 20px 0; overflow: hidden; }
  .hero-in { max-width: 900px; margin: 0 auto; position: relative; z-index: 1; }
  .hero-kicker { font-size: 11px; font-weight: 900; color: var(--or); text-transform: uppercase; letter-spacing: 2px; margin-bottom: 10px; }
  .hero-h { font-family: 'Oswald', sans-serif; font-size: clamp(28px, 4.5vw, 46px); font-weight: 700; color: #fff; line-height: 1.12; margin-bottom: 14px; }
  .hero-h span { color: var(--ol); }
  .hero-p { font-size: 15px; color: rgba(255,255,255,.72); font-weight: 600; line-height: 1.7; max-width: 520px; margin-bottom: 28px; }
  .trust-bar { background: rgba(0,0,0,.25); border-top: 1px solid rgba(255,255,255,.08); padding: 11px 20px; }
  .trust-in { max-width: 900px; margin: 0 auto; display: flex; flex-wrap: wrap; gap: 0; align-items: center; }
  .trust-item { font-size: 11px; font-weight: 800; color: rgba(255,255,255,.72); padding: 3px 16px 3px 0; white-space: nowrap; }
  .trust-sep { width: 1px; height: 14px; background: rgba(255,255,255,.2); margin: 0 16px 0 0; flex-shrink: 0; }

  /* PROGRESS */
  .prog { background: var(--gd); padding: 0 20px; }
  .prog-in { max-width: 900px; margin: 0 auto; display: flex; align-items: center; padding: 12px 0; }
  .prog-step { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
  .pn { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 900; flex-shrink: 0; background: rgba(255,255,255,.15); color: rgba(255,255,255,.5); transition: all .3s; }
  .pn.done { background: var(--or); color: #fff; }
  .pn.act  { background: #fff; color: var(--gd); }
  .pl { font-size: 10px; font-weight: 800; color: rgba(255,255,255,.4); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; transition: color .3s; }
  .pl.act  { color: #fff; }
  .pl.done { color: rgba(255,255,255,.65); }
  .pc { height: 2px; flex: 1; background: rgba(255,255,255,.12); margin: 0 6px; min-width: 8px; }
  .pc.done { background: var(--or); }

  /* LAYOUT */
  .wrap { max-width: 900px; margin: 0 auto; padding: 24px 20px 80px; }

  /* CARDS */
  .card { background: #fff; border-radius: 20px; padding: 24px; margin-bottom: 16px; box-shadow: 0 2px 16px rgba(22,52,34,.08); border: 1.5px solid var(--br); }
  .card-hd { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 20px; }
  .step-num { min-width: 38px; height: 38px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-family: 'Oswald', sans-serif; font-size: 18px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .sn-g { background: linear-gradient(135deg, var(--gb), var(--gc)); }
  .sn-o { background: linear-gradient(135deg, var(--or), var(--ol)); }
  .sn-p { background: linear-gradient(135deg, #7C3AED, #9F67F5); }
  .card-title { font-family: 'Oswald', sans-serif; font-size: 20px; font-weight: 700; color: var(--tx); }
  .card-sub   { font-size: 13px; color: var(--ts); font-weight: 600; margin-top: 4px; line-height: 1.5; }

  /* FORM FIELDS */
  .lbl { font-size: 11px; font-weight: 900; color: var(--ts); text-transform: uppercase; letter-spacing: .8px; margin-bottom: 6px; }
  .inp { width: 100%; padding: 13px 16px; border: 2px solid var(--br); border-radius: 12px; font-family: 'Nunito', sans-serif; font-size: 15px; font-weight: 600; color: var(--tx); background: #FAF8F5; outline: none; transition: border-color .2s; appearance: none; }
  .inp:focus { border-color: var(--gb); }
  .r2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .r3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
  @media (max-width: 580px) { .r3 { grid-template-columns: 1fr 1fr; } .r2 { grid-template-columns: 1fr; } }

  /* OPTION BUTTONS */
  .opts { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px; }
  .opt { flex: 1; min-width: 120px; padding: 15px 12px; border: 2.5px solid var(--br); border-radius: 16px; background: #FAF8F5; cursor: pointer; text-align: center; transition: all .18s; }
  .opt:hover { border-color: var(--gb); background: #F0FAF4; transform: translateY(-1px); }
  .opt.sel { border-color: var(--gb); background: linear-gradient(135deg,#EBF7F0,#D8F0E3); box-shadow: 0 2px 12px rgba(30,77,48,.15); }
  .opt.soft { border-style: dashed; border-color: #A78BFA; background: #FAF5FF; }
  .opt.soft.sel { border-color: #7C3AED; background: linear-gradient(135deg,#F3F0FF,#E9E3FF); }
  .opt-i { font-size: 24px; display: block; margin-bottom: 6px; }
  .opt-t { font-size: 13px; font-weight: 800; color: var(--tx); line-height: 1.3; }
  .opt-d { font-size: 11px; color: var(--ts); font-weight: 600; margin-top: 3px; line-height: 1.4; }
  .qlbl  { font-size: 15px; font-weight: 800; color: var(--tx); margin-bottom: 4px; }
  .qhint { font-size: 12px; color: var(--ts); font-weight: 600; margin-bottom: 10px; line-height: 1.5; }

  /* CHIPS (insulation/windows) */
  .chips { display: flex; flex-wrap: wrap; gap: 7px; }
  .chip { padding: 7px 13px; border-radius: 20px; border: 2px solid var(--br); background: #FAF8F5; font-size: 12px; font-weight: 800; color: var(--ts); cursor: pointer; transition: all .15s; }
  .chip:hover { border-color: var(--gb); }
  .chip.sel { border-color: var(--gb); background: #DFF0E8; color: var(--gd); }
  .chip-note { font-size: 11px; color: var(--ts); font-weight: 600; margin-top: 5px; font-style: italic; line-height: 1.4; }
  .auto-tag { font-size: 10px; font-weight: 800; color: var(--gb); background: #DCF5E4; padding: 3px 9px; border-radius: 20px; border: 1.5px solid #86EFAC; }

  /* SYSTEM BANNER */
  .sys-banner { border-radius: 14px; padding: 14px 18px; margin-top: 14px; border: 2px solid; display: flex; align-items: flex-start; gap: 12px; }
  .sys-icon { font-size: 24px; flex-shrink: 0; line-height: 1; margin-top: 2px; }
  .sys-title { font-size: 15px; font-weight: 800; }
  .sys-desc  { font-size: 12px; font-weight: 600; color: var(--ts); margin-top: 3px; line-height: 1.5; }

  /* UNSURE CTA */
  .unsure-cta { background: linear-gradient(135deg,#F0FAF4,#DCF5E8); border: 2px solid #86EFAC; border-radius: 16px; padding: 18px; margin-top: 16px; display: flex; gap: 14px; align-items: flex-start; }
  .unsure-cta-i { font-size: 28px; flex-shrink: 0; }
  .unsure-cta-t { font-size: 15px; font-weight: 800; color: var(--gd); margin-bottom: 4px; }
  .unsure-cta-s { font-size: 12px; color: var(--ts); font-weight: 600; line-height: 1.5; margin-bottom: 10px; }
  .cta-btns { display: flex; gap: 8px; flex-wrap: wrap; }
  .btn-green { display: inline-block; background: linear-gradient(135deg,var(--gb),var(--gc)); color: #fff; padding: 9px 16px; border-radius: 12px; font-size: 13px; font-weight: 800; text-decoration: none; }
  .btn-outline { display: inline-block; background: rgba(30,77,48,.08); color: var(--gd); padding: 9px 16px; border-radius: 12px; font-size: 13px; font-weight: 800; text-decoration: none; border: 2px solid rgba(30,77,48,.18); }

  /* DISCLAIMER */
  .disclaimer { background: #FFFBEB; border: 2px solid #FCD34D; border-radius: 14px; padding: 12px 16px; margin-bottom: 16px; display: flex; gap: 10px; align-items: flex-start; }
  .disc-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
  .disc-t { font-size: 13px; font-weight: 800; color: #92400E; }
  .disc-s { font-size: 11px; color: #78350F; font-weight: 600; line-height: 1.5; margin-top: 2px; }

  /* BUTTONS */
  .btn-calc { width: 100%; background: linear-gradient(135deg,var(--or),var(--ol)); color: #fff; border: none; border-radius: 16px; padding: 20px; font-family: 'Oswald', sans-serif; font-size: 20px; font-weight: 700; cursor: pointer; box-shadow: 0 6px 24px rgba(217,92,10,.4); transition: all .2s; letter-spacing: .5px; text-transform: uppercase; }
  .btn-calc:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(217,92,10,.55); }
  .btn-rheem { width: 100%; background: linear-gradient(135deg,var(--gd),var(--gb)); color: #fff; border: none; border-radius: 16px; padding: 17px; font-family: 'Oswald', sans-serif; font-size: 17px; font-weight: 700; cursor: pointer; box-shadow: 0 6px 24px rgba(22,52,34,.35); transition: all .2s; margin-top: 12px; letter-spacing: .5px; text-transform: uppercase; }
  .btn-rheem:hover { transform: translateY(-2px); }

  /* RESULTS */
  .results { background: #fff; border-radius: 20px; margin-bottom: 16px; box-shadow: 0 4px 24px rgba(22,52,34,.12); border: 1.5px solid var(--br); overflow: hidden; }
  .res-hero { background: linear-gradient(135deg,var(--gd),#0F2318); padding: 24px 28px; position: relative; overflow: hidden; }
  .res-hero::after { content: '⛰️'; position: absolute; right: -16px; bottom: -28px; font-size: 170px; opacity: .05; pointer-events: none; }
  .res-tag  { font-size: 10px; font-weight: 900; color: var(--or); text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; }
  .res-h    { font-family: 'Oswald', sans-serif; font-size: 28px; font-weight: 700; color: #fff; margin-bottom: 4px; }
  .res-sub  { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 600; font-style: italic; }
  .res-body { padding: 24px 28px; }
  .tn-grid  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 18px; }
  @media (max-width: 480px) { .tn-grid { grid-template-columns: 1fr; } }
  .tn-card  { background: var(--bg); border-radius: 16px; padding: 18px; border: 1.5px solid var(--br); }
  .tn-lbl   { font-size: 11px; font-weight: 900; text-transform: uppercase; letter-spacing: .8px; color: var(--ts); margin-bottom: 8px; }
  .tn-val   { font-family: 'Oswald', sans-serif; font-size: 42px; font-weight: 700; line-height: 1; }
  .tn-unit  { font-size: 16px; font-weight: 600; color: var(--ts); }
  .tn-sub   { font-size: 12px; font-weight: 700; color: var(--ts); margin-top: 4px; }
  .clim-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-bottom: 16px; }
  @media (max-width: 480px) { .clim-row { grid-template-columns: repeat(2,1fr); } }
  .clim-cell { background: var(--bg2); border-radius: 10px; padding: 9px 12px; text-align: center; }
  .clim-l   { font-size: 10px; font-weight: 800; color: var(--ts); text-transform: uppercase; letter-spacing: .4px; }
  .clim-v   { font-size: 13px; font-weight: 800; color: var(--tx); margin-top: 3px; }

  /* RHEEM GRID */
  .rw { background: #fff; border-radius: 20px; margin-bottom: 16px; box-shadow: 0 4px 24px rgba(22,52,34,.12); border: 1.5px solid var(--br); overflow: hidden; }
  .rw-hdr { background: linear-gradient(135deg,var(--gd),#0F2318); padding: 20px 24px; display: flex; gap: 14px; align-items: center; }
  .rw-ico { font-size: 28px; }
  .rw-t   { font-family: 'Oswald', sans-serif; font-size: 22px; font-weight: 700; color: #fff; }
  .rw-s   { font-size: 12px; color: rgba(255,255,255,.6); font-weight: 600; margin-top: 2px; }
  .rgrid  { display: grid; gap: 0; border-top: 1px solid var(--br); }
  .rgrid.cols-3 { grid-template-columns: repeat(3,1fr); }
  .rgrid.cols-2 { grid-template-columns: repeat(2,1fr); }
  @media (max-width: 680px) { .rgrid.cols-3, .rgrid.cols-2 { grid-template-columns: 1fr; } }
  .rcard { border-right: 1px solid var(--br); position: relative; }
  .rcard:last-child { border-right: none; }
  @media (max-width: 680px) { .rcard { border-right: none; border-bottom: 1px solid var(--br); } }
  .rcard-popular::before { content: '⭐ Most Popular'; position: absolute; top: 0; right: 0; background: var(--or); color: #fff; font-size: 10px; font-weight: 900; padding: 4px 10px; border-radius: 0 0 0 10px; letter-spacing: .3px; }
  .rc-hdr  { padding: 16px 18px 14px; }
  .rc-tier { font-family: 'Oswald', sans-serif; font-size: 22px; font-weight: 700; color: #fff; }
  .rc-lbl  { font-size: 11px; font-weight: 800; color: rgba(255,255,255,.72); text-transform: uppercase; letter-spacing: .8px; }
  .rc-body { padding: 16px 18px; }
  .model-box { background: var(--bg); border-radius: 10px; padding: 10px 12px; margin-bottom: 9px; }
  .model-lbl { font-size: 10px; font-weight: 900; color: var(--ts); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 2px; }
  .model-num { font-family: 'Oswald', sans-serif; font-size: 18px; font-weight: 700; color: var(--tx); }
  .model-ser { font-size: 11px; color: var(--ts); font-weight: 600; }
  .no-outdoor-box { background: #F0FDF4; border: 1.5px solid #86EFAC; border-radius: 10px; padding: 10px 14px; margin-bottom: 9px; }
  .no-outdoor-t { font-size: 11px; font-weight: 800; color: #166534; }
  .no-outdoor-s { font-size: 12px; font-weight: 700; color: #166534; }
  .stats { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 9px; }
  .stat { background: var(--bg2); border-radius: 8px; padding: 5px 9px; text-align: center; }
  .stat-l { font-size: 9px; font-weight: 900; color: var(--ts); text-transform: uppercase; letter-spacing: .4px; }
  .stat-v { font-family: 'Oswald', sans-serif; font-size: 14px; font-weight: 700; color: var(--tx); }
  .bullets { margin-bottom: 9px; }
  .bullet { display: flex; gap: 6px; align-items: flex-start; font-size: 12px; font-weight: 600; color: var(--ts); margin-bottom: 4px; line-height: 1.4; }
  .bullet-ck { color: var(--gc); font-weight: 900; flex-shrink: 0; }
  .why-box { font-size: 11px; font-weight: 700; color: var(--ts); background: var(--bg); border-radius: 8px; padding: 8px 10px; margin-bottom: 9px; line-height: 1.4; font-style: italic; }
  .price-box { font-family: 'Oswald', sans-serif; font-size: 16px; font-weight: 700; color: var(--gd); text-align: center; padding: 10px; background: linear-gradient(135deg,#EBF7F2,#D8F0E3); border-radius: 10px; border: 1.5px solid #86EFAC; }

  /* CONTACT FORM */
  .contact-card { background: linear-gradient(135deg,#fff,#F9FFF9); border-radius: 20px; padding: 28px; margin-bottom: 16px; box-shadow: 0 4px 24px rgba(22,52,34,.1); border: 2px solid var(--br); }
  .contact-hdr { display: flex; align-items: center; gap: 14px; margin-bottom: 6px; }
  .contact-ico { font-size: 32px; }
  .contact-t { font-family: 'Oswald', sans-serif; font-size: 24px; font-weight: 700; color: var(--tx); }
  .contact-s { font-size: 14px; color: var(--ts); font-weight: 600; line-height: 1.6; margin-bottom: 20px; }
  .contact-fields { display: flex; flex-direction: column; gap: 14px; }
  .contact-note { font-size: 12px; color: var(--ts); font-weight: 600; text-align: center; margin-top: 8px; line-height: 1.5; }
  .btn-send { width: 100%; background: linear-gradient(135deg,var(--or),var(--ol)); color: #fff; border: none; border-radius: 14px; padding: 18px; font-family: 'Oswald', sans-serif; font-size: 19px; font-weight: 700; cursor: pointer; box-shadow: 0 6px 20px rgba(217,92,10,.4); transition: all .2s; letter-spacing: .5px; }
  .btn-send:hover { transform: translateY(-2px); box-shadow: 0 8px 26px rgba(217,92,10,.55); }
  .sent-box { background: linear-gradient(135deg,#F0FDF4,#D8F5E4); border: 2px solid #86EFAC; border-radius: 16px; padding: 28px; text-align: center; }
  .sent-ico { font-size: 52px; margin-bottom: 12px; }
  .sent-t { font-family: 'Oswald', sans-serif; font-size: 26px; font-weight: 700; color: var(--gd); margin-bottom: 8px; }
  .sent-s { font-size: 14px; color: var(--ts); font-weight: 600; line-height: 1.6; }

  /* ANIMATIONS */
  @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes popIn  { from { opacity:0; transform:scale(.94); } to { opacity:1; transform:scale(1); } }
  @keyframes confetti { 0%,100% { transform:rotate(-3deg) scale(1); } 50% { transform:rotate(3deg) scale(1.04); } }
  .fu  { animation: fadeUp .42s cubic-bezier(.16,1,.3,1) both; }
  .pi  { animation: popIn  .35s cubic-bezier(.16,1,.3,1) both; }
  .cel { animation: confetti .6s ease infinite; display:inline-block; }
`;

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────
function ProgBar({ step }) {
  const steps = ["Your Area","Home Info","Your System","Results"];
  return (
    <div className="prog">
      <div className="prog-in">
        {steps.map((label, i) => {
          const done = step > i+1, act = step === i+1 || (step>=4 && i===3);
          return (
            <div key={i} className="prog-step">
              <div className={`pn ${done?"done":act?"act":""}`}>{done?"✓":i+1}</div>
              <div className={`pl ${done?"done":act?"act":""}`}>{label}</div>
              {i < 3 && <div className={`pc ${done?"done":""}`}/>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────
export default function BullMountainCalc() {
  // Step 1
  const [inPortland, setInPortland] = useState(null);
  const [visitorName, setVisitorName] = useState("");
  // Step 2
  const [sqft, setSqft]       = useState("");
  const [year, setYear]       = useState("");
  const [stories, setStories] = useState("1");
  const [ins, setIns]         = useState("Average");
  const [win, setWin]         = useState("Average");
  const [insNote, setInsNote] = useState("");
  const [winNote, setWinNote] = useState("");
  // Step 3
  const [venting, setVenting]   = useState(null);
  const [outdoor, setOutdoor]   = useState(null);
  // Results
  const [calc, setCalc]     = useState(null);
  const [rheem, setRheem]   = useState(null);
  const [system, setSystem] = useState(null);
  // Contact
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");
  const [note, setNote]     = useState("");
  const [sent, setSent]     = useState(false);

  const resRef  = useRef(null);
  const rhmRef  = useRef(null);
  const ctaRef  = useRef(null);

  // Derived
  const ventingDone  = venting !== null;
  const outdoorDone  = outdoor !== null;
  const systemUnsure = venting === "unsure" || outdoor === "unsure";
  const noOutdoor    = outdoor === "none";

  useEffect(() => {
    if (ventingDone && outdoorDone && !systemUnsure) setSystem(detectSystem(venting, outdoor));
    else setSystem(null);
  }, [venting, outdoor]);

  useEffect(() => { if (calc  && resRef.current) resRef.current.scrollIntoView({ behavior:"smooth", block:"start" }); }, [calc]);
  useEffect(() => { if (rheem && rhmRef.current) rhmRef.current.scrollIntoView({ behavior:"smooth", block:"start" }); }, [rheem]);

  function applyYear(y) {
    const d = deriveFromYear(y);
    if (d) { setIns(d.ins); setWin(d.win); setInsNote(d.insNote); setWinNote(d.winNote); }
  }

  function doCalc() {
    if (!sqft || !year) return;
    setCalc(null); setRheem(null); setSent(false);
    const c = calcLoads({ sqft, stories, ins, windows: win });
    setCalc({ ...c, sqft, year, stories, ins, win });
  }

  function doRheem() {
    if (!calc) return;
    const systemType = noOutdoor ? "furnace-only" : system?.type || "furnace-80";
    const opts = getRheem({ coolTons: calc.coolTons, heatBtu: calc.heatBtu, systemType, venting: venting||"metal", outdoor: outdoor||"no" });
    setRheem(opts);
    setTimeout(() => rhmRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 80);
  }

  function doSend() {
    if (!calc) return;
    const body = buildEmail({ contactName: visitorName, email, phone, note, calc, system, options: rheem || [] });
    const sub  = encodeURIComponent("HVAC Estimate Request — Bull Mountain");
    const bod  = encodeURIComponent(body);
    window.open(`mailto:info@bullmountainheating.com?subject=${sub}&body=${bod}`);
    setSent(true);
    setTimeout(() => ctaRef.current?.scrollIntoView({ behavior:"smooth", block:"start" }), 200);
  }

  const progStep = inPortland!==true ? 1 : (!sqft||!year) ? 2 : (!ventingDone||!outdoorDone) ? 3 : 4;

  const VENTING_OPTS = [
    { id:"metal",  ico:"🔧", title:"Metal flue pipes",     desc:"Round or rectangular metal pipes out through roof or wall" },
    { id:"pvc",    ico:"⬜", title:"White PVC pipes",      desc:"White plastic pipes — usually 2 of them — out the side" },
    { id:"none",   ico:"🌬️", title:"No flue pipes at all", desc:"Just wiring & ducts — fully electric, no gas" },
    { id:"unsure", ico:"🤷", title:"Not sure",             desc:"That's totally okay — we can figure it out together" },
  ];
  const OUTDOOR_OPTS = [
    { id:"yes",    ico:"❄️", title:"Yes, it runs in winter", desc:"I can hear it going when the heat is on" },
    { id:"no",     ico:"☀️", title:"Silent all winter",      desc:"It only runs in summer for the AC" },
    { id:"none",   ico:"🚫", title:"I don't have one",       desc:"No outdoor unit at all right now" },
    { id:"unsure", ico:"🤷", title:"Not sure",               desc:"Never really checked — not sure!" },
  ];

  const B = v => Number(v).toLocaleString();
  const T = v => Number(v).toFixed(1);

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)"}}>
      <style>{CSS}</style>

      {/* ── HERO ── */}
      <div className="hero">
        <div className="hero-in">
          <div className="hero-kicker">⭐ Rheem Pro Partner &nbsp;·&nbsp; Portland Local Since 2003</div>
          <h1 className="hero-h">Find Out Exactly What<br/><span>Your Home Actually Needs</span></h1>
          <p className="hero-p">No guessing, no upselling. Answer a few questions and we'll calculate the right system size for your Portland home — then show you real equipment and real pricing.</p>
        </div>
        <div className="trust-bar">
          <div className="trust-in">
            {["🏠 Family-Owned Since 2003","🌲 Portland & SW Metro","⭐ Rheem Pro Partner","💰 Free Estimates","🔒 No sales pressure"].map((t,i,a) => (
              <div key={t} style={{display:"flex",alignItems:"center"}}>
                <span className="trust-item">{t}</span>
                {i < a.length-1 && <span className="trust-sep"/>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <ProgBar step={progStep}/>

      <div className="wrap">

        {/* ── STEP 1: Are you in Portland? ── */}
        <div className="card fu">
          <div className="card-hd">
            <div className="step-num sn-g">1</div>
            <div>
              <div className="card-title">First things first — are you in the Portland area? 🌲</div>
              <div className="card-sub">We serve Portland, Tigard, Beaverton, Lake Oswego, Tualatin, West Linn & the surrounding SW metro</div>
            </div>
          </div>
          <div className="opts">
            {[{id:true,ico:"✅",title:"Yep, I'm nearby!",desc:"Portland metro or SW suburbs"},{id:false,ico:"📍",title:"No, I'm somewhere else",desc:"Outside the area"}].map(o => (
              <div key={String(o.id)} className={`opt${inPortland===o.id?" sel":""}`} onClick={() => setInPortland(o.id)}>
                <span className="opt-i">{o.ico}</span>
                <div className="opt-t">{o.title}</div>
                <div className="opt-d">{o.desc}</div>
              </div>
            ))}
          </div>
          {inPortland === false && (
            <div className="unsure-cta pi" style={{marginTop:14}}>
              <div className="unsure-cta-i">🏔️</div>
              <div>
                <div className="unsure-cta-t">Heads up — we only serve the Portland metro</div>
                <div className="unsure-cta-s">Bull Mountain is proudly local to Portland and surrounding communities. If you're nearby and think we might be able to help, give us a ring!</div>
                <div className="cta-btns"><a href="tel:5036126677" className="btn-green">📞 (503) 612-6677</a></div>
              </div>
            </div>
          )}
          {inPortland === true && (
            <div style={{marginTop:18}}>
              <div className="lbl">What's your name? <span style={{textTransform:"none",letterSpacing:0,fontWeight:600,color:"var(--ts)"}}>— optional, but nice to meet you!</span></div>
              <input className="inp" value={visitorName} onChange={e=>setVisitorName(e.target.value)} placeholder="e.g. Sarah Johnson" autoComplete="name" style={{borderColor:"var(--gb)"}}/>
            </div>
          )}
        </div>

        {/* ── STEP 2: Home Details ── */}
        {inPortland === true && (
          <div className="card fu" style={{animationDelay:"50ms"}}>
            <div className="card-hd">
              <div className="step-num sn-o">2</div>
              <div>
                <div className="card-title">Tell us about your home 🏡</div>
                <div className="card-sub">We'll auto-fill insulation and windows from your build year — but you can always tweak them</div>
              </div>
            </div>
            <div className="r3" style={{marginBottom:18}}>
              <div>
                <div className="lbl">Square Footage</div>
                <input type="number" className="inp" value={sqft} onChange={e=>setSqft(e.target.value)} placeholder="e.g. 2,200"/>
              </div>
              <div>
                <div className="lbl">Year Built</div>
                <input type="number" className="inp" value={year} onChange={e=>{setYear(e.target.value);if(e.target.value.length===4)applyYear(e.target.value);}} placeholder="e.g. 1998"/>
              </div>
              <div>
                <div className="lbl">Stories</div>
                <select className="inp" value={stories} onChange={e=>setStories(e.target.value)}>
                  {["1","1.5","2","3+"].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
              <span style={{fontSize:14,fontWeight:800,color:"var(--tx)"}}>Insulation & Windows</span>
              {year.length>=4 && <span className="auto-tag">🤖 Auto-set from {year}</span>}
            </div>
            <div className="r2">
              <div>
                <div className="lbl">Insulation Quality</div>
                <div className="chips">
                  {["Poor","Below Average","Average","Good","Excellent / High-Performance"].map(v=>(
                    <div key={v} className={`chip${ins===v?" sel":""}`} onClick={()=>setIns(v)}>{v}</div>
                  ))}
                </div>
                {insNote && <div className="chip-note">📋 {insNote}</div>}
              </div>
              <div>
                <div className="lbl">Windows</div>
                <div className="chips">
                  {["Single-pane / Minimal","Average","Good / Double-pane Low-E"].map(v=>(
                    <div key={v} className={`chip${win===v?" sel":""}`} onClick={()=>setWin(v)}>{v}</div>
                  ))}
                </div>
                {winNote && <div className="chip-note">🪟 {winNote}</div>}
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Current System ── */}
        {inPortland===true && sqft && year && (
          <div className="card fu" style={{animationDelay:"90ms"}}>
            <div className="card-hd">
              <div className="step-num sn-p">3</div>
              <div>
                <div className="card-title">What's your current setup? 🔍</div>
                <div className="card-sub">This helps us point you to the right equipment — don't worry if you're not sure</div>
              </div>
            </div>

            <div style={{marginBottom:20}}>
              <div className="qlbl">What do the pipes on your indoor unit look like?</div>
              <div className="qhint">Peek at your furnace or air handler — what comes out the top or side?</div>
              <div className="opts">
                {VENTING_OPTS.map(o=>(
                  <div key={o.id} className={`opt${o.id==="unsure"?" soft":""}${venting===o.id?" sel":""}`} onClick={()=>setVenting(o.id)}>
                    <span className="opt-i">{o.ico}</span>
                    <div className="opt-t">{o.title}</div>
                    <div className="opt-d">{o.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="qlbl">On a cold winter day with the heat running — does your outdoor unit run too?</div>
              <div className="qhint">Step outside when it's heating. Is the outdoor unit humming, or dead quiet all winter?</div>
              <div className="opts">
                {OUTDOOR_OPTS.map(o=>(
                  <div key={o.id} className={`opt${o.id==="unsure"?" soft":""}${outdoor===o.id?" sel":""}`} onClick={()=>setOutdoor(o.id)}>
                    <span className="opt-i">{o.ico}</span>
                    <div className="opt-t">{o.title}</div>
                    <div className="opt-d">{o.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {systemUnsure && (
              <div className="unsure-cta pi" style={{marginTop:16}}>
                <div className="unsure-cta-i">🤙</div>
                <div>
                  <div className="unsure-cta-t">No worries — we identify systems for fun</div>
                  <div className="unsure-cta-s">One of our techs can tell exactly what you have in about 30 seconds. Free visit, zero pressure. You can still run the calculator — we'll work with typical Portland assumptions.</div>
                  <div className="cta-btns">
                    <a href="https://bullmountainheating.com/ac-and-heating-contact/" target="_blank" rel="noreferrer" className="btn-green">📅 Book Free Visit</a>
                    <a href="tel:5036126677" className="btn-outline">📞 (503) 612-6677</a>
                  </div>
                </div>
              </div>
            )}

            {system && !systemUnsure && (
              <div className="sys-banner pi" style={{background:system.bg, borderColor:system.border}}>
                <div className="sys-icon">{system.icon}</div>
                <div>
                  <div className="sys-title" style={{color:system.color}}>Looks like you have a {system.label}!</div>
                  <div className="sys-desc">{system.desc}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── STEP 4: Calculate ── */}
        {inPortland===true && sqft && year && (
          <div className="card fu" style={{animationDelay:"120ms"}}>
            <div className="card-hd">
              <div className="step-num sn-o">4</div>
              <div>
                <div className="card-title">Ready to find out your number? ⚡</div>
                <div className="card-sub">Manual J-style load calculation, tuned for Portland's climate zone 4C — runs instantly in your browser</div>
              </div>
            </div>
            <div className="disclaimer">
              <div className="disc-icon">📋</div>
              <div>
                <div className="disc-t">Ballpark estimate — not a final quote</div>
                <div className="disc-s">This gives you a solid starting point. A Bull Mountain Comfort Advisor will visit to verify measurements, check ductwork, and lock in the exact sizing before any work begins.</div>
              </div>
            </div>
            <button className="btn-calc" onClick={doCalc}>⚡ Calculate My Home's Load</button>
            <div style={{textAlign:"center",marginTop:10,fontSize:12,color:"var(--ts)",fontWeight:600}}>🔒 Everything runs in your browser — nothing leaves your device until you choose to send it</div>
          </div>
        )}

        {/* ── RESULTS ── */}
        {calc && (
          <div ref={resRef} className="results fu">
            <div className="res-hero">
              <div className="res-tag">Your Load Estimate</div>
              <div className="res-h">
                <span className="cel">🎉</span> Here are your numbers{visitorName ? `, ${visitorName.split(" ")[0]}` : ""}!
              </div>
              <div className="res-sub">
                {calc.sqft} sqft · Built {calc.year} · {PORTLAND.climate_zone} · 88°F / 22°F design conditions · <em style={{color:"#F5A623"}}>Site visit required to confirm</em>
              </div>
            </div>
            <div className="res-body">
              <div className="tn-grid">
                <div className="tn-card">
                  <div className="tn-lbl">❄️ Cooling Load</div>
                  <div className="tn-val" style={{color:"#1D4ED8"}}>{T(calc.coolTons)}<span className="tn-unit"> ton</span></div>
                  <div className="tn-sub">{B(calc.coolBtu)} BTU/hr · {PORTLAND.design_summer_temp}°F design</div>
                </div>
                <div className="tn-card">
                  <div className="tn-lbl">🔥 Heating Load</div>
                  <div className="tn-val" style={{color:"#C2410C",fontSize:30}}>{B(calc.heatBtu)}<span className="tn-unit" style={{fontSize:14}}> BTU/hr</span></div>
                  <div className="tn-sub">{PORTLAND.design_winter_temp}°F design · Furnace sized to next standard up</div>
                </div>
              </div>
              <div className="clim-row">
                {[["🌍 Zone",PORTLAND.climate_zone],["☀️ Summer","88°F design"],["❄️ Winter","22°F design"],["🔥 Htg DD",B(PORTLAND.heating_degree_days)],["❄️ Clg DD",B(PORTLAND.cooling_degree_days)],["💧 Climate","Semi-Humid Marine"]].map(([l,v])=>(
                  <div key={l} className="clim-cell"><div className="clim-l">{l}</div><div className="clim-v">{v}</div></div>
                ))}
              </div>
              <button className="btn-rheem" onClick={doRheem}>🏆 Show Me Rheem Equipment Options →</button>
            </div>
          </div>
        )}

        {/* ── RHEEM OPTIONS ── */}
        {rheem && (
          <div ref={rhmRef} className="rw fu">
            <div className="rw-hdr">
              <div className="rw-ico">🏆</div>
              <div>
                <div className="rw-t">{noOutdoor ? "Your Indoor Unit Options" : "Your Rheem System Options"}</div>
                <div className="rw-s">
                  {noOutdoor
                    ? "Indoor unit only — we match the outdoor unit perfectly at install"
                    : `Sized for ${calc?.coolTons} tons · ${system?.label || "Portland home"}`}
                </div>
              </div>
            </div>
            <div className={`rgrid cols-${rheem.length===2?"2":"3"}`}>
              {rheem.map((opt, i) => (
                <div key={i} className={`rcard${opt.tier==="Better"?" rcard-popular":""}`}>
                  <div className="rc-hdr" style={{background:opt.color}}>
                    <div className="rc-tier">{opt.tier}</div>
                    <div className="rc-lbl">{opt.label}</div>
                  </div>
                  <div className="rc-body">
                    <div className="model-box">
                      <div className="model-lbl">Indoor Unit</div>
                      <div className="model-num">{opt.indoor}</div>
                      <div className="model-ser">{opt.indoorSeries}</div>
                    </div>
                    {opt.outdoor ? (
                      <div className="model-box">
                        <div className="model-lbl">Outdoor Unit</div>
                        <div className="model-num">{opt.outdoor}</div>
                        <div className="model-ser">{opt.outdoorSeries}</div>
                      </div>
                    ) : (
                      <div className="no-outdoor-box">
                        <div className="no-outdoor-t">🔧 Outdoor Unit</div>
                        <div className="no-outdoor-s">Matched at install by Bull Mountain</div>
                      </div>
                    )}
                    <div className="stats">
                      {opt.afue  && <div className="stat"><div className="stat-l">AFUE</div><div className="stat-v">{opt.afue}%</div></div>}
                      {opt.seer2 && <div className="stat"><div className="stat-l">SEER2</div><div className="stat-v">{opt.seer2}</div></div>}
                      {opt.hspf2 && <div className="stat"><div className="stat-l">HSPF2</div><div className="stat-v">{opt.hspf2}</div></div>}
                      {!noOutdoor && <div className="stat"><div className="stat-l">Size</div><div className="stat-v">{calc?.coolTons}T</div></div>}
                    </div>
                    <div className="bullets">
                      {opt.bullets.map((b,j) => <div key={j} className="bullet"><span className="bullet-ck">✓</span><span>{b}</span></div>)}
                    </div>
                    <div className="why-box">💡 {opt.why}</div>
                    <div className="price-box">{opt.priceStr}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CONTACT FORM ── */}
        {rheem && !sent && (
          <div className="contact-card fu">
            <div className="contact-hdr">
              <div className="contact-ico">📬</div>
              <div className="contact-t">Want us to reach out with a real quote?</div>
            </div>
            <div className="contact-s">
              Drop your info below and we'll send everything over — your load calculation, equipment options, and pricing estimate.
              Totally optional. No spam, no pressure, ever. We're neighbors.
            </div>
            <div className="contact-fields">
              <div className="r2">
                <div>
                  <div className="lbl">Email address</div>
                  <input type="email" className="inp" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com"/>
                </div>
                <div>
                  <div className="lbl">Phone number</div>
                  <input type="tel" className="inp" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="(503) 555-0100"/>
                </div>
              </div>
              <div>
                <div className="lbl">Anything else? <span style={{textTransform:"none",letterSpacing:0,fontWeight:600,color:"var(--ts)"}}>— optional</span></div>
                <textarea className="inp" rows={3} value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. 'Our old furnace finally died' or 'Just exploring options'" style={{resize:"vertical"}}/>
              </div>
              <button className="btn-send" onClick={doSend}>
                📬 Send My Estimate to Bull Mountain
              </button>
              <div className="contact-note">
                This opens your email app with everything pre-filled — you just hit send.<br/>
                Or call us directly: <a href="tel:5036126677" style={{color:"var(--gd)",fontWeight:800}}>(503) 612-6677</a>
              </div>
            </div>
          </div>
        )}

        {/* ── SENT CONFIRMATION ── */}
        {sent && (
          <div ref={ctaRef} className="sent-box fu">
            <div className="sent-ico">🎉</div>
            <div className="sent-t">You're all set{visitorName ? `, ${visitorName.split(" ")[0]}` : ""}!</div>
            <div className="sent-s">
              Your estimate is on its way to us. A Bull Mountain Comfort Advisor will follow up soon — usually same day.<br/><br/>
              In the meantime, feel free to call us: <a href="tel:5036126677" style={{color:"var(--gd)",fontWeight:800}}>(503) 612-6677</a>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
